import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { baseUserTodosSummaryQueryKey, getOrgTasksQueryKey } from "../../config";
import {
  useGetDevices,
  useGetRoadmapCards,
  useGetRoadmapStages,
  useGetUsersByOrg,
} from "../../hooks";
import { createAdHocTask } from "../../services/backend";
import { BaseUser, Device } from "../../stores/models";
import type { RawEditorHandle } from "../DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import RawEditor from "../DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { useSnackbar } from "../Snackbar";

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  /** Pre-select this device when opening from device context */
  defaultDeviceId?: string;
};

type CardOption = {
  cardKey: string;
  title: string;
  stageTitle: string;
};

export const CreateTaskModal = ({ open, onClose, orgId, defaultDeviceId }: Props) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceSelectionManuallyCleared, setDeviceSelectionManuallyCleared] =
    useState(false);
  const [selectedCard, setSelectedCard] = useState<CardOption | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<BaseUser | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const descriptionEditorRef = useRef<RawEditorHandle | null>(null);

  // Fetch organization users for assignee dropdown
  const { data: orgUsers, isLoading: isLoadingUsers } = useGetUsersByOrg({
    orgId,
  });

  // Fetch devices for the organization
  const { data: devices, isLoading: isLoadingDevices } = useGetDevices({
    orgId,
  });

  // Use the first device to fetch cards (cards are org-level, just need a device to query)
  const firstDeviceId = devices?.[0]?.id ?? "";

  // Fetch cards and stages using first available device
  // Cards are organization-level config, we just need a device to query the hierarchy
  const { data: cards } = useGetRoadmapCards({
    orgId,
    deviceId: firstDeviceId,
    enabled: !!firstDeviceId,
  });

  const { data: stages } = useGetRoadmapStages({
    orgId,
    deviceId: firstDeviceId,
    enabled: !!firstDeviceId,
  });

  // Auto-select device if only one exists or if defaultDeviceId is provided
  useEffect(() => {
    if (!devices) return;
    if (deviceSelectionManuallyCleared) return;

    // If defaultDeviceId is provided, select that device
    if (defaultDeviceId && !selectedDevice) {
      const defaultDevice = devices.find((d) => d.id === defaultDeviceId);
      if (defaultDevice) {
        setSelectedDevice(defaultDevice);
        return;
      }
    }

    // Otherwise auto-select if only one device exists
    if (devices.length === 1 && !selectedDevice) {
      setSelectedDevice(devices[0]);
    }
  }, [devices, selectedDevice, defaultDeviceId, deviceSelectionManuallyCleared]);

  // Focus the title input when the modal opens
  const handleDialogEntered = () => {
    // Focus after dialog transition completes
    setTimeout(() => {
      // Try to focus using the ref first
      if (titleInputRef.current) {
        titleInputRef.current.focus();
        return;
      }
      // Fallback: find the input element by id or by querying the dialog
      const titleInputById = document.getElementById('create-task-title-input') as HTMLInputElement;
      if (titleInputById) {
        titleInputById.focus();
        return;
      }
      // Last resort: find the first input in the dialog
      const dialog = document.querySelector('[role="dialog"]');
      if (dialog) {
        const titleInput = dialog.querySelector('input[type="text"], textarea') as HTMLInputElement | HTMLTextAreaElement;
        if (titleInput) {
          titleInput.focus();
        }
      }
    }, 0);
  };

  // Build card options grouped by stage
  const cardOptions = useMemo(() => {
    if (!cards || !stages) return [];

    return cards
      .filter((card) => card.enabled)
      .map((card) => {
        const stage = stages.find((s) => s.stageKey === card.parentStageKey);
        return {
          cardKey: card.cardKey,
          title: card.title,
          stageTitle: stage?.title ?? "Unknown Stage",
        };
      })
      .sort((a, b) => {
        // Sort by stage title first, then by card title
        const stageCompare = a.stageTitle.localeCompare(b.stageTitle);
        if (stageCompare !== 0) return stageCompare;
        return a.title.localeCompare(b.title);
      });
  }, [cards, stages]);

  const handleClose = () => {
    setTitle("");
    setDescription("");
    descriptionEditorRef.current?.reloadContent("");
    setDeviceSelectionManuallyCleared(false);
    // Reset to default device or auto-select if single device
    const defaultDevice = defaultDeviceId
      ? devices?.find((d) => d.id === defaultDeviceId) ?? null
      : devices?.length === 1
        ? devices[0]
        : null;
    setSelectedDevice(defaultDevice);
    setSelectedCard(null);
    setSelectedAssignee(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    const descriptionMarkdown =
      descriptionEditorRef.current?.getMarkdownContent() ?? description;

    try {
      await createAdHocTask(orgId, {
        title: title.trim(),
        description: descriptionMarkdown.trim() || null,
        deviceId: selectedDevice?.id ?? null,
        parentCardKey: selectedCard?.cardKey ?? null,
        assigneeId: selectedAssignee?.id ?? null,
      });

      // Invalidate the org tasks queries to refresh the list
      await queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "mine"),
      });
      await queryClient.invalidateQueries({
        queryKey: getOrgTasksQueryKey(orgId, "all"),
      });
      await queryClient.invalidateQueries({
        queryKey: baseUserTodosSummaryQueryKey,
      });

      handleClose();
    } catch (err) {
      const errorMessage = "Failed to create task. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSingleDevice = devices?.length === 1;
  const hasMultipleDevices = devices && devices.length > 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: handleDialogEntered,
      }}
    >
      <DialogTitle>Create Task</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 pt-2">
          <TextField
            id="create-task-title-input"
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            error={!!error && !title.trim()}
            helperText={error && !title.trim() ? error : ""}
            inputRef={(el) => {
              if (el) {
                titleInputRef.current = el;
              }
            }}
          />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              className="mb-1 block"
            >
              Description
            </Typography>
            <RawEditor
              ref={descriptionEditorRef}
              markdown={description}
              onChange={setDescription}
              autoFocus={false}
            />
          </Box>

          {/* Assignee selector */}
          <Autocomplete
            options={orgUsers ?? []}
            getOptionLabel={(user) =>
              `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
            }
            value={selectedAssignee}
            onChange={(_, newValue) => setSelectedAssignee(newValue)}
            loading={isLoadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assignee"
                helperText="Who should complete this task"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
          />

          {/* Roadmap card selector (optional) */}
          {cardOptions.length > 0 && (
            <Autocomplete
              options={cardOptions}
              getOptionLabel={(option) => option.title}
              groupBy={(option) => option.stageTitle}
              value={selectedCard}
              onChange={(_, newValue) => setSelectedCard(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roadmap Card (optional)"
                  helperText="Link this task to a roadmap card"
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.cardKey === value?.cardKey
              }
            />
          )}

          {/* Device selector (optional if card is selected) */}
          {hasMultipleDevices && (
            <Autocomplete
              data-testid="create-task-device-select"
              options={devices ?? []}
              getOptionLabel={(device) => device.name}
              value={selectedDevice}
              onChange={(_, newValue) => {
                setSelectedDevice(newValue);
                setDeviceSelectionManuallyCleared(newValue === null);
              }}
              disabled={isSingleDevice || isLoadingDevices}
              loading={isLoadingDevices}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Device (optional)"
                  helperText={
                    selectedCard && !selectedDevice
                      ? "Without a device, this task will appear for all devices"
                      : "Assign this task to a specific device"
                  }
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          )}

          {/* Info text when single device */}
          {isSingleDevice && selectedDevice && (
            <Typography variant="body2" color="text.secondary">
              This task will be assigned to {selectedDevice.name}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
